import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  BreadcrumbService, PanoramaService, StateService
} from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Company } from 'identity-webapi-client';
import { Subscription } from 'rxjs';
import { EnvironmentName, HomeConfig, ProductName, SituViewWebApiClient, GetSituViewRequest, GetSituViewResponse } from 'situview-webapi-client';
import { HomeStatus, RenderType, SituRenderServiceWebApiClient, GetHomeRequest, GetHomeResponse } from 'siturenderservice-webapi-client';
import { CompleteHomeConfig } from 'src/app/shared/models/complete-homeconfig';
import { ActivatedRoute } from '@angular/router';
import { ConfigService } from 'src/app/services/config.service';

@Component({
  selector: 'situ-view-readonly',
  templateUrl: './situ-view-readonly.component.html',
  styleUrls: ['./situ-view-readonly.component.scss']
})
export class SituViewReadonlyComponent implements OnInit, OnDestroy, OnChanges {
  @Input() routeParams: any;

  pluginManager: any;
  situSystems: any;
  initialised: boolean = false;
  linkToPortal: string | undefined;

  selectedCompany: Company | undefined;
  collapsed: boolean = false;
  originalArtifactId: string | undefined;
  artifactId: string | undefined;
  situViewUrl: string | undefined;

  panoramaProductName: ProductName = ProductName.Panorama;

  homeStatusEnum = HomeStatus;
  renderStatus: HomeStatus | undefined;
  renderStatusRoomAvailable: boolean = true;
  renderStatusProgress: number | undefined;
  renderStatusDescription: string | undefined;
  deviceHasGyro: boolean = false;
  showFloorPlan: boolean = false;
  groundFloorAvailable: boolean = false;
  firstFloorAvailable: boolean = false;
  secondFloorAvailable: boolean = false;
  selectedFloorPlan: number = 0;
  viewerHeight: string = "100vh";
  displayOnly: boolean = false;

  //listener outputs
  viewingAngle: number | undefined;
  verticalViewingAngle: number | undefined;
  selectedPanorama: string | undefined;
  selectedProductManufacturer: string | undefined;
  selectedProductSku: string | undefined;

  //params
  viewingAngleParam: number | undefined;
  verticalViewingAngleParam: number | undefined;
  selectedPanoramaParam: string | undefined;
  selectedProductManufacturerParam: string | undefined;
  selectedProductSkuParam: string | undefined;

  private _subscriptions: Subscription[] = [];

  private _loading = false;

  get loading(): boolean { return this._loading; }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    private clipboard: Clipboard,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private stateService: StateService,
    private panoramaService: PanoramaService,
    private situView: SituViewWebApiClient,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private renderService: SituRenderServiceWebApiClient) { }

  ngOnInit(): void {
    this.processRouteParams();

    this._subscriptions.push(this.panoramaService.onSituViewLoad.subscribe(() => {
      this._subscriptions.push(this.stateService.state$.subscribe((state) => {
        this.selectedCompany = state.company ?? undefined;
        if (!this.initialised) {
          this.init();
        }
      }));
    }));
  }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      this._subscriptions.forEach(subscription => {
        if (subscription) {
          subscription.unsubscribe();
        }
      })
    }
    if (this.initialised) {
      this.pluginManager.removeEventListener("floorplanavailable", this.floorPlanAvailableHandler);
      this.pluginManager.removeEventListener("roompanoramaavailable", this.roomPanoramaAvailableHandler);
      this.pluginManager.removeEventListener("homeconfigloadprogress", this.homeConfigLoadProgressHandler);
      this.pluginManager.removeEventListener("homeloaderror", this.homeLoadErrorHandler);
      this.pluginManager.removeEventListener("unhandlederror", this.unhandledErrorHandler);
      this.pluginManager.removeEventListener("viewinganglechange", this.viewingAngleHandler);
      this.pluginManager.removeEventListener("homeDetails", this.homeDetailsHandler);
      //new debugging fields
      this.pluginManager.removeEventListener("panoramaselected", this.panoramaSelectedHandler);
      this.pluginManager.removeEventListener("productcalloutshown", this.productCalloutShownHandler);
      this.pluginManager.removeEventListener("productcallouthidden", this.productCalloutHiddenHandler);
      // Gyroscope controls
      window.removeEventListener("devicemotion", this.deviceMotionListener);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.routeParams && !changes.routeParams.firstChange) {
      this.processRouteParams(); // Re-run the logic when routeParams changes
      this.waitUntilTrue(() => this.initialised === true, 1000).then(() => {
        if (this.artifactId) { //load by param first
          this.loadHomeIdViaSituView(new CompleteHomeConfig(new HomeConfig(), EnvironmentName.Staging, this.panoramaProductName, true, this.artifactId));
          this.getHomeById(this.artifactId);
        } else{
          console.warn('not a valid home id');
        }
      });
    }
  }

  ngAfterViewInit() {
    this.waitUntilTrue(() => this.initialised === true, 1000).then(() => {
      if (this.artifactId) { //load by param first
        this.loadHomeIdViaSituView(new CompleteHomeConfig(new HomeConfig(), EnvironmentName.Staging, this.panoramaProductName, true, this.artifactId));
        this.getHomeById(this.artifactId);
      } else{
        console.warn('not a valid home id');
      }
    });
  }

  shareHome() {
    if (this.artifactId) {
      let url = `${this.configService.config.situPortalBaseUrl}/situ-view?id=${this.artifactId}`;
      this.clipboard.copy(url);
      this.showMessage("Copied config to clipboard!");
    }
  };

  toggleFullscreen() {
    this.pluginManager.toggleFullScreen();
  };

  toggleGyro() {
    this.pluginManager.toggleGyro();
  };

  toggleFloorplan() {
    this.showFloorPlan = !this.showFloorPlan;
  };

  toggleCollapse() {
    this.collapsed = !this.collapsed;
  };

  selectFloorPlan(floor: number) {
    this.selectedFloorPlan = floor;
  }

  copyDebugUrl() {
    const baseUrl = this.configService.config.situLinkBaseUrl 
      ? `https://view.${this.configService.config.situLinkBaseUrl}`
      : this.configService.config.situPortalBaseUrl;
  
    const url = new URL(baseUrl);
    url.pathname = this.artifactId!;
  
    if (this.selectedPanorama) {
      url.searchParams.set('panorama', this.selectedPanorama);
  
      if (this.viewingAngle) {
        url.searchParams.set('hlookat', this.viewingAngle.toString());
      }
  
      if (this.verticalViewingAngle) {
        url.searchParams.set('vlookat', this.verticalViewingAngle.toString());
      }
  
      if (this.selectedProductManufacturer) {
        url.searchParams.set('manufacturer', this.selectedProductManufacturer);
      }
  
      if (this.selectedProductSku) {
        url.searchParams.set('sku', this.selectedProductSku);
      }
    }
  
    this.clipboard.copy(url.toString());
    this.showMessage("Copied share location to clipboard!");
  }

  openInPortal() {
    window.open(this.linkToPortal, '_blank');
  }

  waitUntilTrue(condition: () => boolean, timeout: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const interval = setInterval(() => {
        if (condition()) {
          clearInterval(interval);
          resolve();
        } else {
          timeout -= 1000;
          if (timeout <= 0) {
            clearInterval(interval);
            reject(new Error("Timeout"));
          }
        }
      }, 1000);
    });
  }
  
  private getHomeById(artifactId: string) {
    let request = new GetSituViewRequest();
    request.renderedOutputId = artifactId;
    request.includeHistory = true;

    return this.situView.getSituView(request)
      .then((response: GetSituViewResponse) => {
        if (response.success && response?.files?.length && response.files.length > 0) {
          this.linkToPortal = `${this.configService.config.situPortalBaseUrl}/situ-view?id=${this.artifactId}`;
          this.versionHistory = response.versionHistory;
          this.currentHistoryIndex = artifactId;

          const historyKeys = Object.keys(this.versionHistory || {});
          if (historyKeys.length > 1) {
            this.latestVersionAvailable = true;
            this.rightHistoryThumbIndex = historyKeys[historyKeys.length - 1];
            this.latestHistoryIndex = historyKeys[historyKeys.length - 1];
          }
        } else {
          this.getHomeByIdRenderService(artifactId);
        }
      })
      .catch((err: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your render. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  latestVersionAvailable: boolean = false;
  versionHistory?: { [key: string]: Date; } | undefined;
  currentHistoryIndex: string = "";
  latestHistoryIndex: string = "";
  rightHistoryThumbIndex: string = "";
  showHistory: boolean = false;
  historyToggle: boolean = false;
  getThumbnailUrl(artifactId: string): string {
    return `${this.configService.config.situViewWebApiBaseUrl}/home/getthumb?artifactId=${artifactId}`;
  }

  loadThisVersion(artifactId?: string) {
    if (artifactId) {
      this.currentHistoryIndex = artifactId;
      this.loadHomeIdViaSituView(new CompleteHomeConfig(new HomeConfig(), EnvironmentName.Staging, this.panoramaProductName, true, artifactId), true);
    }
  }

  scrollLeft() {
    if (!this.versionHistory) return;
  
    const historyKeys = Object.keys(this.versionHistory);
    const currentIndex = historyKeys.indexOf(this.rightHistoryThumbIndex);
  
    if (currentIndex > 0) {
      this.rightHistoryThumbIndex = historyKeys[currentIndex - 1];  // Move to previous item
    }
  }
  scrollRight() {
    if (!this.versionHistory) return;
  
    const historyKeys = Object.keys(this.versionHistory);
    const currentIndex = historyKeys.indexOf(this.rightHistoryThumbIndex);
  
    if (currentIndex < historyKeys.length - 1) {
      this.rightHistoryThumbIndex = historyKeys[currentIndex + 1];  // Move to next item
    }
  }
  scrollLeftDisabled(): boolean {
    if (!this.versionHistory) return true;
  
    const historyKeys = Object.keys(this.versionHistory);
    const currentIndex = historyKeys.indexOf(this.rightHistoryThumbIndex);
    return currentIndex <= 0;  // Disable if the first item is reached
  }
  scrollRightDisabled(): boolean {
    if (!this.versionHistory) return true;
  
    const historyKeys = Object.keys(this.versionHistory);
    const currentIndex = historyKeys.indexOf(this.rightHistoryThumbIndex);
    return currentIndex >= historyKeys.length - 1;  // Disable if the last item is reached
  }
  getVersionDate(artifactId: string): Date {
    return this.versionHistory![artifactId];
  }

  private getHomeByIdRenderService(artifactId: string) {
    let request = new GetHomeRequest();
    request.homeId = artifactId;

    return this.renderService.getHome(request)
      .then((response: GetHomeResponse) => {
        if (response.success && response?.homeConfigDetails?.files?.length && response.homeConfigDetails?.files.length > 0) {
          let url = response?.homeConfigDetails?.files[0]?.url;
          if (url) {
            this.linkToPortal = `${this.configService.config.situPortalBaseUrl}/panorama-demo?builder=${this.parseUrl(url)?.builder}&home=${this.parseUrl(url)?.home}&masterFile=${this.parseUrl(url)?.masterFile}&options=${this.parseUrl(url)?.options}&themes=${this.parseUrl(url)?.themes}&room=kitchen&env=0&render=0`;
          }
        }
      })
      .catch((err: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your render. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  parseUrl(url: string) {
    const parts = url.split('/')?.reverse();
    const builder = parts[6];
    const home = parts[5];
    const masterFile = parts[4];
    const options = parts[3];
    const themes = parts[2].split("~").join(",");
    const homeConfig = {
        builder,
        home,
        masterFile,
        options,
        themes
    };

    return homeConfig;
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  private loadHomeIdViaSituView(homeConfig: CompleteHomeConfig, forceRoomSettings: boolean = false) {
    if (!homeConfig?.artifactId) return;
    let roomSettings = new this.situSystems.RoomSettings();
    if (forceRoomSettings) {
      roomSettings.name = this.selectedPanorama;
      roomSettings.viewingAngle = this.viewingAngle;
      roomSettings.verticalViewingAngle = this.verticalViewingAngle;
    } else if ((this.selectedPanoramaParam && this.viewingAngleParam)) {
      roomSettings.name = this.selectedPanoramaParam;
      roomSettings.viewingAngle = this.viewingAngleParam;
      roomSettings.verticalViewingAngle = this.verticalViewingAngleParam;
    }
    this.pluginManager.load(new situVIEWRequest(
      homeConfig.homeConfig, 
      "kitchen", 
      roomSettings?.name ? [roomSettings] : null, 
      false, 
      RenderType.BestAvailable, 
      homeConfig.environment, 
      homeConfig?.artifactId, 
      forceRoomSettings ? roomSettings.name : this.selectedPanoramaParam
    ));
  }

  private init() {
    this.situSystems = this.panoramaService.situSystems;
    if (!this.pluginManager) {
      this.pluginManager = this.panoramaService.createPluginManager();
    }

    let settings = new this.situSystems.PanoramaSettings();
    settings.imageSize = 4096; // Typically 1024, 2048, 4096
    settings.fieldOfView = 75;
    settings.minFieldOfView = 40;
    settings.maxFieldOfView = 110;
    settings.adjustableFieldOfView = false;
    settings.enableGyro = true;

    let defaultFloorplanSettings = new this.situSystems.FloorplanSettings();

    this.pluginManager.addPlugin(new this.situSystems.PanoramaViewer("viewer-readonly", settings));
    this.pluginManager.addPlugin(new this.situSystems.SVGFloorPlan("ground-floor-plan", this.situSystems.Floors.GROUND, true, defaultFloorplanSettings));
    this.pluginManager.addPlugin(new this.situSystems.SVGFloorPlan("first-floor-plan", this.situSystems.Floors.FIRST, true, defaultFloorplanSettings));
    this.pluginManager.addPlugin(new this.situSystems.SVGFloorPlan("second-floor-plan", this.situSystems.Floors.SECOND, true, defaultFloorplanSettings));

    this.pluginManager.addEventListener("floorplanavailable", this.floorPlanAvailableHandler);
    this.pluginManager.addEventListener("roompanoramaavailable", this.roomPanoramaAvailableHandler);
    this.pluginManager.addEventListener("homeconfigloadprogress", this.homeConfigLoadProgressHandler);
    this.pluginManager.addEventListener("homeloaderror", this.homeLoadErrorHandler);
    this.pluginManager.addEventListener("unhandlederror", this.unhandledErrorHandler);
    this.pluginManager.addEventListener("viewinganglechange", this.viewingAngleHandler);
    this.pluginManager.addEventListener("homeDetails", this.homeDetailsHandler);
    //new debugging fields
    this.pluginManager.addEventListener("panoramaselected", this.panoramaSelectedHandler);
    this.pluginManager.addEventListener("productcalloutshown", this.productCalloutShownHandler);
    this.pluginManager.addEventListener("productcallouthidden", this.productCalloutHiddenHandler);
    // Gyroscope controls
    window.addEventListener("devicemotion", this.deviceMotionListener);

    this.initialised = true;
  }

  private deviceMotionListener: (event: DeviceMotionEvent) => void = (event: DeviceMotionEvent) => {
    if (event?.rotationRate?.alpha || event?.rotationRate?.beta || event?.rotationRate?.gamma) {
      if (!this.deviceHasGyro) {
        this.deviceHasGyro = true;
      }
    }
  };

  private floorPlanAvailableHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    if (e?.detail?.floorPlanName) {
      this.renderStatusRoomAvailable = true;
      switch (e?.detail?.floorPlanName) {
        case "GROUND":
          this.groundFloorAvailable = true;
          break;
        case "FIRST":
          this.firstFloorAvailable = true;
          break;
        case "SECOND":
          this.secondFloorAvailable = true;
          break;
      }
    }
  };

  private roomPanoramaAvailableHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    this.renderStatusRoomAvailable = true;
  };

  private homeConfigLoadProgressHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    if (e?.detail) {
      const homeProgress = e.detail as HomeProgress;
      if (homeProgress) {
        if (homeProgress.percentComplete === -1) {
          this.renderStatus = HomeStatus.Failed;
          this.renderStatusDescription = homeProgress.description;
        } else if (homeProgress.percentComplete === 100) {
          this.renderStatus = undefined;
          this.renderStatusProgress = undefined;
          this.renderStatusDescription = undefined;
        } else if (homeProgress.percentComplete >= 2 && homeProgress.percentComplete <= 99) {
          this.renderStatus = HomeStatus.InProgress;
          this.renderStatusProgress = homeProgress.percentComplete;
          this.renderStatusDescription = homeProgress.description;
        } else {
          this.renderStatus = HomeStatus.Queued;
          this.renderStatusDescription = homeProgress.description;
        }
      }
    }
  };

  private homeLoadErrorHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    if (e?.isTrusted) {
      if (e?.detail) {
        this.renderStatus = HomeStatus.Failed;
        this.renderStatusDescription = `Sorry! We failed to render this home. ${e.detail}`;
      } else {
        this.renderStatus = HomeStatus.Failed;
        this.renderStatusDescription = "Sorry! We failed to render this home. Please try again.";
      }
    }
  };

  private unhandledErrorHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    if (e?.isTrusted) {
      if (e?.detail) {
        this.renderStatus = HomeStatus.Failed;
        this.renderStatusDescription = `Sorry! We failed to render this home. ${e.detail}`;
      } else {
        this.renderStatus = HomeStatus.Failed;
        this.renderStatusDescription = "Sorry! We failed to render this home. Please try again.";
      }
    }
  };

  private viewingAngleHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    if (e?.detail?.viewingAngle) {
      this.viewingAngle = e.detail.viewingAngle;
      this.verticalViewingAngle = e.detail.verticalViewingAngle;
    }
  };

  private homeDetailsHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    if (e?.detail?.artifactId) {
      if (this.artifactId !== e.detail.artifactId) this.artifactId = e.detail.artifactId;
    }
  };

  private panoramaSelectedHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    if (e?.detail?.panoramaName && this.selectedPanorama !== e.detail.panoramaName) {
      this.selectedPanorama = e.detail.panoramaName;
    }
  };

  private productCalloutShownHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    if (e?.detail?.manufacturer && e?.detail?.sku) {
      this.selectedProductManufacturer = e.detail.manufacturer;
      this.selectedProductSku = e.detail.sku;
    }
  };

  private productCalloutHiddenHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    if (e?.detail?.manufacturer && e?.detail?.sku) {
      this.selectedProductManufacturer = undefined;
      this.selectedProductSku = undefined;
    }
  };

  private processRouteParams(): void {
    // Process artifactId
    if (this.route.snapshot.queryParams['id']) {
      this.artifactId = this.route.snapshot.queryParams['id'];
    } else if (this.routeParams?.['id']) {
      this.artifactId = this.routeParams['id'];
    }
    this.originalArtifactId = this.artifactId;

    // Process panorama
    const panorama = this.route.snapshot.queryParams['panorama'] ?? this.routeParams?.['panorama'];
    if (panorama) {
      this.selectedPanoramaParam = panorama.toLowerCase();
    }

    // Process hlookat
    const hlookatParam = this.route.snapshot.queryParams['hlookat'] ?? this.routeParams?.['hlookat'];
    if (hlookatParam && !isNaN(parseFloat(hlookatParam))) {
      this.viewingAngleParam = parseFloat(hlookatParam);
    }

    // Process vlookat
    const vlookatParam = this.route.snapshot.queryParams['vlookat'] ?? this.routeParams?.['vlookat'];
    if (vlookatParam && !isNaN(parseFloat(vlookatParam))) {
      this.verticalViewingAngleParam = parseFloat(vlookatParam);
    }

    // Process manufacturer
    const manufacturer = this.route.snapshot.queryParams['manufacturer'] ?? this.routeParams?.['manufacturer'];
    if (manufacturer) {
      this.selectedProductManufacturerParam = manufacturer.toLowerCase();
    }

    // Process product
    const product = this.route.snapshot.queryParams['product'] ?? this.routeParams?.['product'];
    if (product) {
      this.selectedProductSkuParam = product.toLowerCase();
    }

    // Process heightoverride
    const heightoverride = this.routeParams?.['heightoverride'];
    if (heightoverride) {
      this.displayOnly = heightoverride !== "100vh";
      this.viewerHeight = heightoverride.toLowerCase();
    } else {
        this.displayOnly = this.routeParams['quickview'];
    }

    // Process history toggle
    if (this.route.snapshot.queryParams['history']) {
      this.historyToggle = true;
    }
  }
}

export class HomeProgress {
  constructor(public description: string, public percentComplete: number) {
  }
}

export class situVIEWRequest {
  constructor(public homeConfig: HomeConfig, public startingRoom: any, public roomSettings: any, public forceRefresh: boolean, public renderType: RenderType, public environmentName: EnvironmentName, public homeId?: string, public startingPanorama?: string, public editorMode?: boolean) { }
}